// APFormField kit — RHF-wired field components on the shadcn Form pattern
// (spec § Forms & data: successor to the v3 Form.Item-embedding wrappers).
// Labels / placeholders / error messages arrive already translated — the kit
// never hardcodes user-facing English.
import { ReactNode } from 'react';
import {
  useFormContext,
  type FieldPath,
  type FieldValues,
  type RegisterOptions,
} from 'react-hook-form';

import { Checkbox } from '@/components/ui/checkbox';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/cn';
import { APFloatingField, type FloatingMode } from './APFloatingField';
import { usePasswordReveal } from './usePasswordReveal';

/** Label placement. `floating` is the v4 default (spec § Decisions 2026-07-02):
 *  the label rests in the placeholder position and floats to the border on
 *  focus-or-value. `stacked` keeps the label-above look for dense contexts.
 *  Toggle-shaped fields (checkbox / switch / radio cards) never float. */
export type APFieldVariant = 'floating' | 'stacked';

export interface APFieldBaseProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> {
  name: TName;
  /** Already-translated label. Omit to render the control without a label row. */
  label?: ReactNode;
  /** Already-translated helper text under the control. */
  description?: ReactNode;
  /** RHF validation rules — messages must be already-translated strings. */
  rules?: RegisterOptions<TFieldValues, TName>;
  variant?: APFieldVariant;
  /** Required asterisk on the label. Defaults from `rules.required`. */
  required?: boolean;
  disabled?: boolean;
  className?: string;
  'data-testid'?: string;
}

export const fieldTestId = (testId: string | undefined, name: string) => testId ?? `field-${name}`;

interface APFieldShellProps {
  label?: ReactNode;
  description?: ReactNode;
  className?: string;
  testId: string;
  /** Defaults to `stacked` so non-floating consumers are unaffected — the
   *  text-like fields below opt into `floating` themselves. */
  variant?: APFieldVariant;
  required?: boolean;
  floatingMode?: FloatingMode;
  alignTop?: boolean;
  children: ReactNode;
}

/** Shared label + control + description + message shell (exported for sibling field kits). */
export const APFieldShell = ({
  label,
  description,
  className,
  testId,
  variant = 'stacked',
  required,
  floatingMode = 'peer',
  alignTop,
  children,
}: APFieldShellProps) => (
  // content-start: a taller sibling grid cell must not stretch the control row.
  <FormItem className={cn('content-start', className)} data-testid={testId}>
    {label && variant === 'floating' ? (
      <APFloatingField label={label} required={required} mode={floatingMode} alignTop={alignTop}>
        {children}
      </APFloatingField>
    ) : (
      <>
        {label && <FormLabel>{label}</FormLabel>}
        {children}
      </>
    )}
    {description && <FormDescription>{description}</FormDescription>}
    <FormMessage />
  </FormItem>
);

/** Control classes for the `peer`-tracked floating variant: the label IS the
 *  placeholder, so the control's own placeholder is a blank sentinel kept
 *  transparent (it only exists so `:placeholder-shown` tracks emptiness). */
export const floatingPeerClasses = 'peer placeholder:text-transparent';

export function APFormInput<TFieldValues extends FieldValues = FieldValues>({
  name,
  label,
  description,
  rules,
  variant = 'floating',
  required,
  disabled,
  className,
  placeholder,
  type = 'text',
  'data-testid': testId,
}: APFieldBaseProps<TFieldValues> & { placeholder?: string; type?: string }) {
  const { control } = useFormContext<TFieldValues>();
  const floating = variant === 'floating' && !!label;
  return (
    <FormField
      control={control}
      name={name}
      rules={rules}
      render={({ field }) => (
        <APFieldShell
          label={label}
          description={description}
          className={className}
          testId={fieldTestId(testId, name)}
          variant={variant}
          required={required ?? !!rules?.required}
        >
          <FormControl>
            <Input
              {...field}
              value={field.value ?? ''}
              type={type}
              placeholder={floating ? ' ' : placeholder}
              className={floating ? floatingPeerClasses : undefined}
              disabled={disabled}
            />
          </FormControl>
        </APFieldShell>
      )}
    />
  );
}

/** Password input, masked with a show/hide reveal (v3 APPassword parity —
 *  never mask without a reveal: admins must be able to read auto-seeded
 *  passwords). Always floating; the eye button rides APFloatingField's
 *  relative wrapper as a sibling slot (see usePasswordReveal). */
export function APFormPassword<TFieldValues extends FieldValues = FieldValues>({
  name,
  label,
  description,
  rules,
  required,
  disabled,
  className,
  autoComplete = 'new-password',
  'data-testid': testId,
}: APFieldBaseProps<TFieldValues> & { autoComplete?: string }) {
  const { control } = useFormContext<TFieldValues>();
  const { type, toggle } = usePasswordReveal(fieldTestId(testId, name), { disabled });
  return (
    <FormField
      control={control}
      name={name}
      rules={rules}
      render={({ field }) => (
        <APFieldShell
          label={label}
          description={description}
          className={className}
          testId={fieldTestId(testId, name)}
          variant="floating"
          required={required ?? !!rules?.required}
        >
          <FormControl>
            <Input
              {...field}
              value={field.value ?? ''}
              type={type}
              autoComplete={autoComplete}
              placeholder=" "
              className={cn(floatingPeerClasses, 'pe-10')}
              disabled={disabled}
            />
          </FormControl>
          {toggle}
        </APFieldShell>
      )}
    />
  );
}

export function APFormNumber<TFieldValues extends FieldValues = FieldValues>({
  name,
  label,
  description,
  rules,
  variant = 'floating',
  required,
  disabled,
  className,
  placeholder,
  min,
  max,
  step,
  'data-testid': testId,
}: APFieldBaseProps<TFieldValues> & {
  placeholder?: string;
  min?: number;
  max?: number;
  step?: number;
}) {
  const { control } = useFormContext<TFieldValues>();
  const floating = variant === 'floating' && !!label;
  return (
    <FormField
      control={control}
      name={name}
      rules={rules}
      render={({ field }) => (
        <APFieldShell
          label={label}
          description={description}
          className={className}
          testId={fieldTestId(testId, name)}
          variant={variant}
          required={required ?? !!rules?.required}
        >
          <FormControl>
            <Input
              {...field}
              value={field.value ?? ''}
              onChange={(event) => {
                // Keep '' as null so the wire payload stays a number-or-null,
                // never '' — and never NaN: partial input ('-', '1e') parses to
                // NaN, which would sit in RHF state and dodge min/max rules.
                const parsed = event.target.valueAsNumber;
                field.onChange(event.target.value === '' || Number.isNaN(parsed) ? null : parsed);
              }}
              type="number"
              inputMode="decimal"
              placeholder={floating ? ' ' : placeholder}
              min={min}
              max={max}
              step={step}
              disabled={disabled}
              className={cn('tabular-nums', floating && floatingPeerClasses)}
            />
          </FormControl>
        </APFieldShell>
      )}
    />
  );
}

// APFormDate moved to ./APFormDate.tsx (shadcn Calendar composition — the
// native `<input type="date">` implementation was retired 2026-07-03).

export function APFormTextarea<TFieldValues extends FieldValues = FieldValues>({
  name,
  label,
  description,
  rules,
  variant = 'floating',
  required,
  disabled,
  className,
  placeholder,
  rows,
  'data-testid': testId,
}: APFieldBaseProps<TFieldValues> & { placeholder?: string; rows?: number }) {
  const { control } = useFormContext<TFieldValues>();
  const floating = variant === 'floating' && !!label;
  return (
    <FormField
      control={control}
      name={name}
      rules={rules}
      render={({ field }) => (
        <APFieldShell
          label={label}
          description={description}
          className={className}
          testId={fieldTestId(testId, name)}
          variant={variant}
          required={required ?? !!rules?.required}
          alignTop
        >
          <FormControl>
            <Textarea
              {...field}
              value={field.value ?? ''}
              placeholder={floating ? ' ' : placeholder}
              className={floating ? floatingPeerClasses : undefined}
              rows={rows}
              disabled={disabled}
            />
          </FormControl>
        </APFieldShell>
      )}
    />
  );
}

export interface APSelectOption {
  value: string;
  /** Already-translated label. */
  label: ReactNode;
  disabled?: boolean;
}

export function APFormSelect<TFieldValues extends FieldValues = FieldValues>({
  name,
  label,
  description,
  rules,
  variant = 'floating',
  required,
  disabled,
  className,
  placeholder,
  options,
  onValueChange,
  serialize = (value: any) => value ?? undefined,
  deserialize = (raw: string) => raw,
  'data-testid': testId,
}: APFieldBaseProps<TFieldValues> & {
  placeholder?: string;
  options: APSelectOption[];
  /** Intercept a change before it lands in the form (v3 FeatureStatusSelect-style confirm flows). */
  onValueChange?: (value: string, commit: (value: string) => void) => void;
  /** Form value → select item value (radix select values are strings). */
  serialize?: (value: any) => string | undefined;
  /** Select item value → form value (e.g. '3' → number). */
  deserialize?: (raw: string) => any;
}) {
  const { control } = useFormContext<TFieldValues>();
  const floating = variant === 'floating' && !!label;
  return (
    <FormField
      control={control}
      name={name}
      rules={rules}
      render={({ field }) => (
        <APFieldShell
          label={label}
          description={description}
          className={className}
          testId={fieldTestId(testId, name)}
          variant={variant}
          required={required ?? !!rules?.required}
          floatingMode="group"
        >
          <Select
            value={serialize(field.value)}
            onValueChange={(raw) => {
              if (onValueChange) {
                onValueChange(raw, (value) => field.onChange(deserialize(value)));
              } else {
                field.onChange(deserialize(raw));
              }
            }}
            disabled={disabled}
          >
            <FormControl>
              <SelectTrigger className="w-full">
                {/* Floating: the label occupies the resting slot — Radix still
                    sets [data-placeholder] on the empty trigger, which is what
                    APFloatingField tracks. */}
                <SelectValue placeholder={floating ? undefined : placeholder} />
              </SelectTrigger>
            </FormControl>
            <SelectContent>
              {options.map((option) => (
                <SelectItem key={option.value} value={option.value} disabled={option.disabled}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </APFieldShell>
      )}
    />
  );
}

export function APFormCheckbox<TFieldValues extends FieldValues = FieldValues>({
  name,
  label,
  description,
  rules,
  disabled,
  className,
  'data-testid': testId,
}: APFieldBaseProps<TFieldValues>) {
  const { control } = useFormContext<TFieldValues>();
  return (
    <FormField
      control={control}
      name={name}
      rules={rules}
      render={({ field }) => (
        <FormItem className={className} data-testid={fieldTestId(testId, name)}>
          <div className="flex items-center gap-2">
            <FormControl>
              <Checkbox
                checked={!!field.value}
                onCheckedChange={field.onChange}
                disabled={disabled}
              />
            </FormControl>
            {label && <FormLabel className="font-normal">{label}</FormLabel>}
          </div>
          {description && <FormDescription>{description}</FormDescription>}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

export function APFormSwitch<TFieldValues extends FieldValues = FieldValues>({
  name,
  label,
  description,
  rules,
  disabled,
  className,
  'data-testid': testId,
}: APFieldBaseProps<TFieldValues>) {
  const { control } = useFormContext<TFieldValues>();
  return (
    <FormField
      control={control}
      name={name}
      rules={rules}
      render={({ field }) => (
        <FormItem className={className} data-testid={fieldTestId(testId, name)}>
          <div className="flex items-center justify-between gap-4">
            <div className="grid gap-1">
              {label && <FormLabel>{label}</FormLabel>}
              {description && <FormDescription>{description}</FormDescription>}
            </div>
            <FormControl>
              <Switch
                checked={!!field.value}
                onCheckedChange={field.onChange}
                disabled={disabled}
              />
            </FormControl>
          </div>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

export interface APRadioOption {
  value: string;
  /** Already-translated label. */
  label: ReactNode;
  disabled?: boolean;
}

/** Plain inline radio group (label + horizontal options) — the v4 successor
 *  to v3's bare `Radio.Group` rows. Card-shaped options → APFormRadioCards. */
export function APFormRadioGroup<TFieldValues extends FieldValues = FieldValues>({
  name,
  label,
  description,
  rules,
  disabled,
  className,
  options,
  serialize = String,
  deserialize = (raw) => raw,
  'data-testid': testId,
}: APFieldBaseProps<TFieldValues> & {
  options: APRadioOption[];
  /** Form value → radio item value (radix radio values are strings). */
  serialize?: (value: any) => string;
  /** Radio item value → form value (e.g. 'true' → boolean). */
  deserialize?: (raw: string) => any;
}) {
  const { control } = useFormContext<TFieldValues>();
  return (
    <FormField
      control={control}
      name={name}
      rules={rules}
      render={({ field }) => (
        <APFieldShell
          label={label}
          description={description}
          className={className}
          testId={fieldTestId(testId, name)}
        >
          <FormControl>
            <RadioGroup
              value={
                field.value === undefined || field.value === null ? '' : serialize(field.value)
              }
              onValueChange={(raw) => field.onChange(deserialize(raw))}
              disabled={disabled}
              className="flex flex-wrap items-center gap-4"
            >
              {options.map((option) => (
                <Label
                  key={option.value}
                  className={cn(
                    'flex cursor-pointer items-center gap-2 font-normal',
                    (disabled || option.disabled) && 'cursor-not-allowed opacity-50'
                  )}
                  data-testid={`${fieldTestId(testId, name)}-option-${option.value}`}
                >
                  <RadioGroupItem value={option.value} disabled={option.disabled} />
                  <span className="text-sm">{option.label}</span>
                </Label>
              ))}
            </RadioGroup>
          </FormControl>
        </APFieldShell>
      )}
    />
  );
}

export interface APRadioCardOption {
  value: string;
  /** Already-translated label. */
  label: ReactNode;
  /** Already-translated supporting copy. */
  description?: ReactNode;
  icon?: ReactNode;
  disabled?: boolean;
  /** Already-translated tooltip shown on a disabled card explaining WHY it's
   *  disabled (rendered only while the card is disabled). */
  disabledReason?: ReactNode;
}

/** Radio group rendered as selectable cards (one per option). */
export function APFormRadioCards<TFieldValues extends FieldValues = FieldValues>({
  name,
  label,
  description,
  rules,
  disabled,
  className,
  options,
  optionsClassName,
  extra,
  'data-testid': testId,
}: APFieldBaseProps<TFieldValues> & {
  options: APRadioCardOption[];
  /** Extends/overrides the option-grid columns (e.g. `lg:grid-cols-3`). */
  optionsClassName?: string;
  /** Non-radio tile rendered in the same option grid, after the options
   *  (e.g. the Custom Rubric card) — keeps mixed option sets on one layout. */
  extra?: ReactNode;
}) {
  const { control } = useFormContext<TFieldValues>();
  return (
    <FormField
      control={control}
      name={name}
      rules={rules}
      render={({ field }) => (
        <APFieldShell
          label={label}
          description={description}
          className={className}
          testId={fieldTestId(testId, name)}
        >
          <FormControl>
            <RadioGroup
              value={field.value ?? ''}
              onValueChange={field.onChange}
              disabled={disabled}
              className={cn('grid gap-2 sm:grid-cols-2', optionsClassName)}
            >
              {options.map((option) => {
                const isDisabled = disabled || option.disabled;
                const card = (
                  <Label
                    key={option.value}
                    className={cn(
                      'flex cursor-pointer gap-3 rounded-lg border p-3 font-normal transition-colors hover:bg-ap-accent-a50 has-data-[state=checked]:border-primary has-data-[state=checked]:bg-ap-primary-a5',
                      // Single-line options center radio/icon/label on one axis;
                      // described options keep first-line (top) alignment.
                      option.description ? 'items-start' : 'items-center',
                      isDisabled && 'cursor-not-allowed opacity-50'
                    )}
                    data-testid={`${fieldTestId(testId, name)}-option-${option.value}`}
                  >
                    <RadioGroupItem
                      value={option.value}
                      disabled={option.disabled}
                      className={option.description ? 'mt-0.5' : undefined}
                    />
                    {option.icon}
                    <span className="grid gap-1">
                      <span className="text-sm leading-none font-medium">{option.label}</span>
                      {option.description && (
                        <span className="text-sm text-muted-foreground">{option.description}</span>
                      )}
                    </span>
                  </Label>
                );
                // Disabled cards with a reason explain themselves on hover.
                if (isDisabled && option.disabledReason) {
                  return (
                    <Tooltip key={option.value}>
                      <TooltipTrigger asChild>{card}</TooltipTrigger>
                      <TooltipContent>{option.disabledReason}</TooltipContent>
                    </Tooltip>
                  );
                }
                return card;
              })}
              {extra}
            </RadioGroup>
          </FormControl>
        </APFieldShell>
      )}
    />
  );
}
